import { useState, useEffect } from 'react'
import {
  get2FAStatus,
  enable2FA,
  disable2FA,
} from '../api/settings'
import type { TOTPStatusResponse, EnableTOTPResponse } from '@/shared/api/generated'
import { getErrorMessage } from '@/shared/api/client'
import { toast } from '@/shared/stores/toastStore'

type Step = 'status' | 'enable' | 'qr' | 'disable'

export function TwoFactorSettings() {
  const [status, setStatus] = useState<TOTPStatusResponse | null>(null)
  const [step, setStep] = useState<Step>('status')
  const [password, setPassword] = useState('')
  const [qrData, setQrData] = useState<EnableTOTPResponse | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    get2FAStatus().then(setStatus).catch(err => toast.error(getErrorMessage(err)))
  }, [])

  async function handleEnable(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await enable2FA(password)
      setQrData(data)
      setStep('qr')
      setPassword('')
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleDisable(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await disable2FA(password)
      setStatus({ status: 'disabled', enabled: false, backup_codes_remaining: 0 })
      setStep('status')
      setPassword('')
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  function handleDone() {
    setStatus({ status: 'enabled', enabled: true, backup_codes_remaining: qrData?.backup_codes.length ?? 0 })
    setQrData(null)
    setStep('status')
  }

  if (!status) return null

  if (step === 'qr' && qrData) {
    return (
      <div data-testid="2fa-qr">
        <h3>✅ 2FA Enabled</h3>
        <p>Scan this QR code with your authenticator app:</p>
        <div className="centered">
          <img src={qrData.qr_code} alt="QR Code" className="qr-image" />
        </div>
        <p>
          <strong>Manual entry key:</strong>{' '}
          <code>{qrData.secret}</code>
        </p>
        <h4>Backup Codes</h4>
        <p className="text-warn"><strong>⚠️ Save these codes! They can only be used once.</strong></p>
        <ul className="backup-codes">
          {qrData.backup_codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
        <button onClick={handleDone} className="btn-primary">Done</button>
      </div>
    )
  }

  if (step === 'enable') {
    return (
      <div data-testid="2fa-enable">
        <h3>Enable Two-Factor Authentication</h3>
        <form onSubmit={handleEnable}>
          <label htmlFor="2fa-enable-pw">Password (to confirm)</label>
          <input id="2fa-enable-pw" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          {error && <p className="text-danger">{error}</p>}
          <div className="button-row">
            <button type="button" className="btn-secondary" onClick={() => { setStep('status'); setError(''); setPassword('') }}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Enabling...' : 'Enable 2FA'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  if (step === 'disable') {
    return (
      <div data-testid="2fa-disable">
        <h3>Disable Two-Factor Authentication</h3>
        <p className="text-warn">⚠️ This will make your account less secure.</p>
        <form onSubmit={handleDisable}>
          <label htmlFor="2fa-disable-pw">Password (to confirm)</label>
          <input id="2fa-disable-pw" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          {error && <p className="text-danger">{error}</p>}
          <div className="button-row">
            <button type="button" className="btn-secondary" onClick={() => { setStep('status'); setError(''); setPassword('') }}>Cancel</button>
            <button type="submit" className="btn-danger" disabled={loading}>
              {loading ? 'Disabling...' : 'Disable 2FA'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div data-testid="2fa-status">
      <h3>Two-Factor Authentication</h3>
      <p>
        Status:{' '}
        <span className={status.enabled ? 'text-success' : 'muted'}>
          {status.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </p>
      {status.enabled && (
        <p className="muted">Backup codes remaining: {status.backup_codes_remaining}</p>
      )}
      {status.enabled ? (
        <button className="btn-outline-danger" onClick={() => setStep('disable')}>Disable 2FA</button>
      ) : (
        <button className="btn-primary" onClick={() => setStep('enable')}>Enable 2FA</button>
      )}
    </div>
  )
}
